import Wheel, { type WheelPalette } from './Wheel'

import type { WHEEL_MODES } from '../config'
import type { AnimationDrawParams } from './Animation'

export default class WheelInteractive extends Wheel {
  private bufferCanvas: HTMLCanvasElement | null = null

  private animationState: {
    rotationAngle: number
    fadeProgress: number
    pointerShift: number
    lights: Record<number, number>
    stage?: 's1' | 's2' | 's3'
  } = {
    rotationAngle: 0,
    fadeProgress: 0,
    pointerShift: 0,
    lights: {},
  }

  animationConfig = {
    spin: {
      spinsPerSec: 1.25,
      timeS1: 2500,
      timeS3: 2500,
      vfCoef: 0, // 0 <= vfCoef <= 1; vf - final velocity; vf = vfCoef * vi(spinsPerSec)
    },
    fade: {
      time: 400,
    },
    pointer: {
      time: 500,
    },
    lights: {
      groupDuration: 1300,
      groupDelay: 350, // should be > groupDuration / (Math.floor(lightsN / groupLen) + 1)
      groupLen: 3,
    },
  }

  private createCombinedAnimation<AnimationState extends Partial<typeof this.animationState>>(
    ...animationInputs: {
      getStatePerDraw: (drawParams: AnimationDrawParams) => AnimationState
      onPlay?: () => void
      onStop?: () => void
      onKill?: () => void
    }[]
  ) {
    const { createAnimation } = this.constructor.helpers

    // compose callbacks
    const callbacks = ['getStatePerDraw', 'onPlay', 'onStop', 'onKill'] as const
    const { getStatePerDraw, onPlay, onStop, onKill } = animationInputs.reduce((acc, cur, i) => {
      if (i === 0) {
        return acc
      }

      callbacks.forEach((cbKey) => {
        if (cbKey === 'getStatePerDraw') {
          const accCb = acc[cbKey]
          const curCb = cur[cbKey]

          acc[cbKey] = (params) => {
            const accReturn = accCb(params)
            const curReturn = curCb(params)

            return {
              ...accReturn,
              ...curReturn,
            }
          }
        } else {
          const accCb = acc[cbKey]
          const curCb = cur[cbKey]

          acc[cbKey] = () => {
            accCb?.()
            curCb?.()
          }
        }
      })

      return acc
    }, animationInputs[0])

    const animation = createAnimation({
      draw: (args) => {
        this.draw({
          ...this.animationState,
          ...getStatePerDraw(args),
        })
      },
    })

    return {
      ...animation,
      play: () => {
        onPlay()
        animation.play()
      },
      stop: () => {
        onStop()
        animation.stop()
      },
      kill: () => {
        onKill()
        animation.kill()
      },
    }
  }

  private createLightsAnimation() {
    const { getRandomIntInclusive } = this.constructor.helpers
    const len = this.config.lights
    const { groupDuration, groupDelay, groupLen } = this.animationConfig.lights

    const state: {
      intervalId: ReturnType<typeof setInterval>
      groups: {
        initTime: number
        ids: number[]
      }[]
    } = {
      intervalId: null,
      groups: [],
    }

    const setNewGroup = (() => {
      // generate groups
      const predefinedGroups = ((): number[][] => {
        const base = (len / groupLen) ^ 0
        const left = len % groupLen
        const baseGroups = [...Array(base)].map((_, i) => {
          return [...Array(groupLen)].map((__, j) => i + j * base)
        })
        const leftGroups = [...Array(left)].map((_, i) => {
          return [len - 1 - i]
        })
        const groups = [...baseGroups, ...leftGroups]

        // Fisher–Yates shuffle
        let curIndex = groups.length
        while (curIndex--) {
          const randIndex = getRandomIntInclusive(curIndex)

          ;[groups[curIndex], groups[randIndex]] = [groups[randIndex], groups[curIndex]]
        }

        return groups
      })()

      const nextGroupGen = (function* () {
        while (true) {
          yield* predefinedGroups
        }
      })()

      return () => {
        const initTime = Date.now()
        const ids = nextGroupGen.next().value

        state.groups.push({
          initTime,
          ids,
        })
      }
    })()

    const setInitialState = () => {
      setNewGroup()
    }

    const setStateUpdater = () => {
      state.intervalId = setInterval(() => {
        const d = Date.now()
        // sanitize groups
        state.groups = state.groups.filter((group) => d - group.initTime < groupDuration)
        // add new group
        setNewGroup()
      }, groupDelay)
    }

    // need to specify the outcome Type to avoid the type checking collisions
    const getStatePerDraw = (): Partial<typeof this.animationState> => {
      const d = Date.now()
      const lights: Record<number, number> = {}

      state.groups.forEach((group) => {
        const timeDiff = d - group.initTime
        const progress = timeDiff >= groupDuration ? 1 : timeDiff / groupDuration
        const dp = progress >= 0.5 ? 1 - progress : progress

        group.ids.forEach((id) => {
          lights[id] = dp * 2
        })
      })

      return { lights }
    }

    const onKill = () => {
      clearInterval(state.intervalId)
    }

    return {
      getStatePerDraw,
      onPlay: () => {
        setInitialState()
        setStateUpdater()
      },
      onStop: onKill,
      onKill,
    }
  }

  private createPointerAnimation() {
    const { time } = this.animationConfig.pointer
    const { shift } = this.metrics.pointer

    // need to specify the outcome Type to avoid the type checking collisions
    const getStatePerDraw = ({ timeDiff }: AnimationDrawParams): Partial<typeof this.animationState> => {
      const dir = ((timeDiff / time) ^ 0) % 2 ? 1 : -1
      const progress = (timeDiff % time) / time
      const dirProgress = dir === -1 ? progress : 1 - progress

      return {
        pointerShift: shift * dirProgress,
      }
    }

    return { getStatePerDraw }
  }

  private createFadeAnimation(params: { onAnimationEnd: () => void }) {
    const { getBezierEasing, createAnimation } = this.constructor.helpers
    const { time } = this.animationConfig.fade
    const easeOut = getBezierEasing(0, 0, 0.58, 1.0)

    const animation = createAnimation({
      draw: ({ progress }) => {
        const fadeProgress = easeOut(progress)
        this.draw({ ...this.animationState, fadeProgress })
      },
      timer: time,
      onEnd: () => {
        this.draw({ ...this.animationState, fadeProgress: 1 })
        this.animationState.fadeProgress = 1

        params.onAnimationEnd()
      },
    })

    return {
      ...animation,
      play: () => {
        if (typeof this.contentData.winIndex === 'number') {
          animation.play()
        }
      },
    }
  }

  private createSpinAnimation({
    onAnimationEnd,
    getWinnerAsync,
  }: {
    onAnimationEnd: () => void
    getWinnerAsync: () => Promise<number>
  }) {
    const { delay, getBezierEasing, createAnimation } = this.constructor.helpers
    const { spinsPerSec, timeS1, timeS3, vfCoef } = this.animationConfig.spin
    const { segments } = this.contentData

    const segmentAngle = 360 / segments.length
    const easinIn = getBezierEasing(0.5, 0, 0.3, 0.3)
    const vi = (360 * spinsPerSec) / 1000
    const vf = vi * vfCoef

    const state: {
      stage: 's1' | 's2' | 's3'
      timeS3: number
      initTimeDiffS3: number
      initAngleS3: number
    } = {
      stage: 's1',
      timeS3: 0,
      initTimeDiffS3: 0,
      initAngleS3: 0,
    }

    // quadratic integrated velocity:
    const getDistanceS3 = (dt: number, tt = timeS3) => {
      const b = (vf - vi) / tt ** 2

      return vi * dt + (b * dt ** 3) / 3
    }
    // [getTimeS3] derived from [getDistanceS3]
    const getTimeS3 = (d: number) => {
      return d / (vi + (vf - vi) / 3)
    }
    const maxDistanceS3 = getDistanceS3(timeS3)

    const getWinSegmentPos = () => (segments.length - this.contentData.winIndex) * segmentAngle

    const getRotationAngle = (timeDiff: number) => {
      /**** STAGE 1 BEGIN ***/
      if (state.stage === 's1') {
        const progressS1 = timeDiff / timeS1

        return easinIn(progressS1) * vi * timeS1
      }
      /**** STAGE 1 END *****/

      /**** STAGE 2 BEGIN ***/
      if (state.stage === 's2') {
        // linear motion
        return vi * timeDiff
      }
      /**** STAGE 2 END *****/

      /**** STAGE 3 BEGIN ***/
      const timeDiffS3 = timeDiff - state.initTimeDiffS3

      const angle = state.initAngleS3 + getDistanceS3(timeDiffS3, state.timeS3)
      // preserve an angle for the global state
      this.animationState.rotationAngle = angle

      return angle
      /**** STAGE 3 END *****/
    }

    const animation = createAnimation({
      draw: (params) => {
        const rotationAngle = Math.round((getRotationAngle(params.timeDiff) % 360) * 100) / 100

        if (state.stage !== 's3') {
          state.initTimeDiffS3 = params.timeDiff
          state.initAngleS3 = rotationAngle
        }

        this.draw({ ...this.animationState, rotationAngle })
      },
      onEnd: () => {
        // make sure the wheel is stopped in the correct position!!!
        const winSegmentPos = getWinSegmentPos()
        const curSegmentPos = this.animationState.rotationAngle % 360 || 360 // 1 ... 360
        const curSegmentIdx = segments.length - Math.round(curSegmentPos / segmentAngle)

        let isAcceptablePos = this.contentData.winIndex === curSegmentIdx
        if (isAcceptablePos) {
          // check the pointer does not point to an edge between segments
          const winSegmentPosL = (winSegmentPos - segmentAngle / 2) % 360
          const winSegmentPosR = (winSegmentPos + segmentAngle / 2) % 360
          const checkAngleDiffL = Math.abs(winSegmentPosL - curSegmentPos)
          const checkAngleDiffR = Math.abs(winSegmentPosR - curSegmentPos)

          // the shift from the edge should be more the 1 degree
          isAcceptablePos = checkAngleDiffL > 1 && checkAngleDiffR > 1
        }

        if (!isAcceptablePos) {
          this.animationState.rotationAngle = winSegmentPos
          this.draw(this.animationState)
        }

        onAnimationEnd()
      },
    })

    let abortController: AbortController = null
    const cancellablePromise = <T>(promise: Promise<T>, signal: AbortSignal): Promise<T> => {
      return new Promise((resolve, reject) => {
        signal.addEventListener('abort', () => reject(new Error('Cancelled')), { once: true })
        promise.then(resolve, reject)
      })
    }

    return {
      ...animation,
      play: async () => {
        abortController = new AbortController()
        try {
          /**** STAGE 1 BEGIN ***/
          animation.play()

          state.stage = 's1'
          await cancellablePromise(delay(timeS1), abortController.signal)
          /**** STAGE 1 END *****/

          /**** STAGE 2 BEGIN ***/
          state.stage = 's2'
          this.contentData.winIndex = await cancellablePromise(getWinnerAsync(), abortController.signal)
          /**** STAGE 2 END *****/

          /**** STAGE 3 BEGIN ***/
          const segmentPos = getWinSegmentPos()
          const minDistanceS3 =
            segmentPos < state.initAngleS3 ? 360 - state.initAngleS3 + segmentPos : segmentPos - state.initAngleS3
          const distanceS3 = Math.round((maxDistanceS3 - minDistanceS3) / 360) * 360 + minDistanceS3

          state.timeS3 = getTimeS3(distanceS3)

          state.stage = 's3'
          await cancellablePromise(delay(state.timeS3), abortController.signal)

          animation.stop()
          /**** STAGE 3 END *****/
        } catch (error) {
          console.log('error: ', error)

          animation.kill()
        }
      },
      kill: () => {
        animation.kill()
        abortController?.abort()
      },
    }
  }

  private createAnimation({ onAnimationEnd, getWinnerAsync }: Parameters<typeof this.init>[1]) {
    const pointerAnimation = this.createPointerAnimation()
    const lightsAnimation = this.createLightsAnimation()
    const finalAnimation = this.createCombinedAnimation(lightsAnimation, pointerAnimation)
    const fadeAnimation = this.createFadeAnimation({
      onAnimationEnd: () => {
        this.animationState.stage = 's3'
        finalAnimation.play()
        onAnimationEnd()
      },
    })
    const spinAnimation = this.createSpinAnimation({
      onAnimationEnd: () => {
        this.animationState.stage = 's2'
        fadeAnimation.play()
      },
      getWinnerAsync,
    })

    const cleanUp = () => {
      spinAnimation.kill()
      fadeAnimation.kill()
      finalAnimation.kill()
    }

    return {
      start: () => {
        this.animationState.stage = 's1'
        this.bufferCanvas = null

        spinAnimation.play()
      },
      onLeaveTab: () => {
        // abort the spin stage
        if (this.animationState.stage === 's1') {
          spinAnimation.kill()
        }
      },
      onFocusTab: () => {
        // skip no spin stage
        if (this.animationState.stage !== 's1') {
          return
        }

        if (typeof this.contentData.winIndex === 'number') {
          // set final state
          const segmentLen = this.contentData.segments.length
          const segmentPos = (segmentLen - this.contentData.winIndex) * (360 / segmentLen)

          this.animationState = {
            ...this.animationState,
            stage: 's3',
            fadeProgress: 1,
            rotationAngle: segmentPos,
          }
          finalAnimation.play()
          onAnimationEnd()
        } else {
          // restart from beginning
          spinAnimation.play()
        }
      },
      cleanUp,
    }
  }

  private wireEvents(params: Parameters<typeof this.init>[1]) {
    const { isTouchDevice, isPointInCircle } = this.constructor.helpers
    const animation = this.createAnimation(params)

    const dpr = this.canvasParams.dpr
    const paddingTop = this.metrics.paddingTop
    const buttonInnerR = this.metrics.button.innerR

    type CanvasMouseEvent = MouseEvent & { target: HTMLCanvasElement }

    const isCursorOverButton = ((_buttonInnerR, _paddingTop) => {
      return (event: CanvasMouseEvent) => {
        const { clientX, clientY, target } = event
        const { top, left, height, width } = target.getBoundingClientRect()
        const { x0, y0 } = { x0: left + width / 2, y0: top + height / 2 + _paddingTop / 2 }

        return isPointInCircle(clientX, clientY, x0, y0, _buttonInnerR)
      }
    })(buttonInnerR / dpr, paddingTop / dpr)

    const onClick = (event: CanvasMouseEvent) => {
      if (isCursorOverButton(event)) {
        animation.start()

        this.node.removeEventListener('click', onClick)
        this.node.removeEventListener('mousemove', onMouseMove)

        event.target.style.cursor = ''
      }
    }

    const onMouseMove = (event: CanvasMouseEvent) => {
      if (isCursorOverButton(event)) {
        event.target.style.cursor = 'pointer'
      } else {
        event.target.style.cursor = ''
      }
    }

    this.node.addEventListener('click', onClick)

    if (!isTouchDevice()) {
      this.node.addEventListener('mousemove', onMouseMove)
    }

    const visibilityChangeListener = () => {
      if (document.hidden) {
        animation.onLeaveTab()
      } else {
        animation.onFocusTab()
      }
    }
    document.addEventListener('visibilitychange', visibilityChangeListener)

    this.setCleanUpObserver(() => {
      animation.cleanUp()
      document.removeEventListener('visibilitychange', visibilityChangeListener)
    })
  }

  draw(params = this.animationState) {
    // the [this.drawSomething] order matters
    switch (this.animationState.stage) {
      case 's1':
      // spin
      // eslint-disable-next-line no-fallthrough
      case 's2':
        // fade
        this.ctx.clearRect(0, 0, this.canvasParams.w, this.canvasParams.h)
        this.drawTopBar()
        this.drawBorder()
        this.drawContent(params)
        this.drawButton(params.fadeProgress)
        this.drawLights()
        this.drawPointer()

        break
      case 's3':
        // celebration

        this.ctx.clearRect(0, 0, this.canvasParams.w, this.canvasParams.h)

        if (!this.bufferCanvas) {
          this.bufferCanvas = document.createElement('canvas')
          this.bufferCanvas.width = this.canvasParams.w
          this.bufferCanvas.height = this.canvasParams.h
          const bufferCtx = this.bufferCanvas.getContext('2d')

          // Save current context and switch to buffer
          const mainCtx = this.ctx
          this.ctx = bufferCtx

          // Draw the static layout once
          this.drawTopBar()
          this.drawBorder()
          this.drawContent(params)
          this.drawButton(params.fadeProgress)
          this.drawRewardContent()

          // Restore main context
          this.ctx = mainCtx
        }

        this.ctx.drawImage(this.bufferCanvas, 0, 0)

        this.drawLights(params.lights)
        this.drawPointer(params.pointerShift)

        break
      default:
        // static
        super.draw()
    }
  }

  init<ContentData extends typeof this.contentData>(
    node: HTMLCanvasElement,
    params: {
      data: ContentData
      onAnimationEnd: () => void
      getWinnerAsync: () => Promise<number>
      palette?: Partial<WheelPalette>
      wheelSrc?: string
      buttonSrc?: string
      mode?: WHEEL_MODES
    },
  ) {
    super.init(node, params)

    if (params.wheelSrc) {
      const img = new Image()
      img.src = params.wheelSrc

      img.onload = () => {
        this.wheelImg = img

        if (!node.id) {
          node.setAttribute('id', 'wow-main-canvas')
        }

        this.wireEvents(params)

        this.draw()
      }

      return
    }

    if (!node.id) {
      node.setAttribute('id', 'wow-main-canvas')
    }

    this.wireEvents(params)

    this.draw()
  }
}
